import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaEnvelope, FaClock, FaPaperPlane, FaBan, FaSyncAlt, FaWhatsapp } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

const nowLocalInput = () => {
  const d = new Date(Date.now() + 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultSubject = (id) => (id ? `Preventivo n. ${id} - BetaLumina` : "Preventivo - BetaLumina");

// BETALUMINA_EMAIL_PREVENTIVI_PRO_FRONT_V3B_20261018
const defaultMessage = (id, cliente) => {
  const saluto = cliente ? `Buongiorno ${cliente},` : "Buongiorno,";
  return [
    saluto,
    "",
    `in allegato trasmettiamo il nostro preventivo n. ${id}.`,
    "",
    "Restiamo a disposizione per qualsiasi chiarimento o approfondimento.",
  ].join("\n");
};

const formatItalianDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).replace("T", " ");
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PreventivoEmailSection = ({
  preventivoId,
  destinatario = "",
  cliente = "",
  isDirty = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [status, setStatus] = useState({ active: null, latestSent: null, sourceModified: false });
  const [to, setTo] = useState(destinatario || "");
  const [subject, setSubject] = useState(defaultSubject(preventivoId));
  const [message, setMessage] = useState(defaultMessage(preventivoId, cliente));
  const [scheduledAt, setScheduledAt] = useState("");

  const token = () => localStorage.getItem("token");

  const authHeaders = (json = false) => ({
    Authorization: `Bearer ${token() || ""}`,
    ...(json ? { "Content-Type": "application/json" } : {}),
  });

  const apiRequest = async (path, options = {}) => {
    const response = await fetch(`${API_URL}${path}`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || `Errore HTTP ${response.status}`);
    }
    return data;
  };

  const loadStatus = async ({ quiet = false } = {}) => {
    if (!preventivoId) return;
    if (!quiet) setStatusLoading(true);
    try {
      const data = await apiRequest(`/api/preventivi/${preventivoId}/email-programmata`, {
        headers: authHeaders(false),
      });
      setStatus(data || { active: null, latestSent: null, sourceModified: false });

      if (!quiet) {
        if (data?.active) {
          setTo(data.active.destinatario || destinatario || "");
          setSubject(data.active.oggetto || defaultSubject(preventivoId));
          setMessage(data.active.messaggio || defaultMessage(preventivoId, cliente));
          setScheduledAt(data.active.scheduledAt || "");
        } else {
          setTo(destinatario || data?.preventivo?.email || "");
          setSubject(defaultSubject(preventivoId));
          setMessage(defaultMessage(preventivoId, cliente || data?.preventivo?.cliente || ""));
        }
      }
    } catch (error) {
      if (!quiet) toast.error(error.message || "Errore caricamento invio email");
    } finally {
      if (!quiet) setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (!preventivoId) return undefined;
    loadStatus();
    const timer = setInterval(() => loadStatus({ quiet: true }), 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preventivoId]);

  useEffect(() => {
    if (!status.active && destinatario) setTo(destinatario);
  }, [destinatario, status.active]);

  useEffect(() => {
    if (!status.active) {
      setSubject(defaultSubject(preventivoId));
      setMessage(defaultMessage(preventivoId, cliente));
    }
  }, [preventivoId, cliente, status.active]);

  const canOperate =
    Boolean(preventivoId) &&
    !loading &&
    !statusLoading &&
    !isDirty &&
    status.active?.stato !== "in_invio";

  const programButtonLabel = useMemo(
    () => (status.active ? "Aggiorna programmazione" : "Programma invio"),
    [status.active]
  );

  const schedule = async () => {
    if (isDirty) {
      toast.warning("Salva prima le modifiche del preventivo: il PDF da inviare deve corrispondere alla versione salvata.");
      return;
    }
    if (!to.trim()) return toast.error("Inserisci l'indirizzo email del cliente.");
    if (!scheduledAt) return toast.error("Scegli giorno e orario dell'invio.");
    if (!subject.trim()) return toast.error("Inserisci l'oggetto dell'email.");

    setLoading(true);
    try {
      await apiRequest(`/api/preventivi/${preventivoId}/email-programmata`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          action: "schedule",
          destinatario: to.trim(),
          oggetto: subject.trim(),
          messaggio: message,
          scheduledAt,
        }),
      });
      toast.success("Invio email programmato sul NAS.");
      await loadStatus({ quiet: true });
    } catch (error) {
      toast.error(error.message || "Errore programmazione email");
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    if (isDirty) {
      toast.warning("Salva prima le modifiche del preventivo, poi invia l'email.");
      return;
    }
    if (!to.trim()) return toast.error("Inserisci l'indirizzo email del cliente.");
    if (!subject.trim()) return toast.error("Inserisci l'oggetto dell'email.");
    if (!window.confirm(`Inviare adesso il preventivo n. ${preventivoId} a ${to.trim()}?`)) return;

    setLoading(true);
    try {
      const data = await apiRequest(`/api/preventivi/${preventivoId}/email-programmata`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          action: "send_now",
          destinatario: to.trim(),
          oggetto: subject.trim(),
          messaggio: message,
        }),
      });
      if (data?.job?.stato === "inviato") {
        toast.success("Preventivo inviato via email.");
      } else {
        toast.info("Invio preso in carico. In caso di errore il NAS ritentera automaticamente.");
      }
      await loadStatus({ quiet: true });
    } catch (error) {
      toast.error(error.message || "Errore invio email");
    } finally {
      setLoading(false);
    }
  };

  const cancelScheduled = async () => {
    if (!status.active?.id) return;
    if (!window.confirm("Annullare l'invio email programmato?")) return;
    setLoading(true);
    try {
      await apiRequest(
        `/api/preventivi/${preventivoId}/email-programmata/${status.active.id}`,
        { method: "DELETE", headers: authHeaders(false) }
      );
      toast.success("Invio programmato annullato.");
      setScheduledAt("");
      await loadStatus({ quiet: true });
    } catch (error) {
      toast.error(error.message || "Errore annullamento invio");
    } finally {
      setLoading(false);
    }
  };

  const refreshSnapshot = async () => {
    if (!status.active?.id) return;
    if (isDirty) {
      toast.warning("Salva prima il preventivo, poi aggiorna il PDF programmato.");
      return;
    }
    setLoading(true);
    try {
      await apiRequest(
        `/api/preventivi/${preventivoId}/email-programmata/${status.active.id}/refresh-snapshot`,
        { method: "POST", headers: authHeaders(true), body: JSON.stringify({}) }
      );
      toast.success("PDF programmato aggiornato alla versione corrente.");
      await loadStatus({ quiet: true });
    } catch (error) {
      toast.error(error.message || "Errore aggiornamento PDF programmato");
    } finally {
      setLoading(false);
    }
  };

  if (!preventivoId) return null;

  const active = status.active;
  const activeClass = active?.stato === "errore" ? "error" : active?.stato === "in_invio" ? "sending" : "scheduled";

  return (
    <div className="modal-section preventivo-email-programmata-section">
      <div className="preventivo-email-programmata-header">
        <div>
          <h3><FaEnvelope /> Invio email al cliente</h3>
          <p>
            Invio dal NAS anche a PC spento. Il PDF viene congelato quando programmi l'email,
            con il logo BetaLumina e il collegamento WhatsApp nel messaggio.
          </p>
        </div>
        <span className="preventivo-email-nas-badge">NAS automatico</span>
      </div>

      {isDirty && (
        <div className="preventivo-email-warning">
          Salva prima il preventivo: programmazione e invio usano sempre l'ultima versione salvata.
        </div>
      )}

      {active && (
        <div className={`preventivo-email-status-card ${activeClass}`}>
          <div className="preventivo-email-status-top">
            <strong>
              {active.stato === "errore"
                ? "Invio in attesa di nuovo tentativo"
                : active.stato === "in_invio"
                  ? "Invio in corso"
                  : "Invio programmato"}
            </strong>
            <span>{active.stato}</span>
          </div>
          <div className="preventivo-email-status-grid">
            <span><FaClock /> {formatItalianDateTime(active.scheduledAt)}</span>
            <span>{active.destinatario}</span>
            <span>PDF congelato: {active.hasSnapshot ? "si" : "no"}</span>
            {active.attempts > 0 && <span>Tentativi: {active.attempts}</span>}
          </div>
          {active.lastError && <div className="preventivo-email-error-text">{active.lastError}</div>}
          {status.sourceModified && (
            <div className="preventivo-email-source-warning">
              Il preventivo e stato modificato dopo la programmazione. Partira il PDF congelato precedente finche non premi "Aggiorna PDF programmato".
            </div>
          )}
          <div className="preventivo-email-inline-actions">
            <button type="button" onClick={refreshSnapshot} disabled={!canOperate} className="preventivo-email-secondary-btn">
              <FaSyncAlt /> Aggiorna PDF programmato
            </button>
            <button type="button" onClick={cancelScheduled} disabled={loading} className="preventivo-email-danger-btn">
              <FaBan /> Annulla invio
            </button>
          </div>
        </div>
      )}

      {status.latestSent && !active && (
        <div className="preventivo-email-last-sent">
          Ultimo invio: <strong>{formatItalianDateTime(status.latestSent.sentAt)}</strong> a {status.latestSent.destinatario}
        </div>
      )}

      <div className="preventivo-email-grid">
        <div className="preventivo-email-field preventivo-email-field-wide">
          <label>Destinatario</label>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="cliente@email.it" />
        </div>

        <div className="preventivo-email-field preventivo-email-field-wide">
          <label>Oggetto</label>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={255} />
        </div>

        <div className="preventivo-email-field preventivo-email-field-wide">
          <label>Messaggio</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} />
        </div>

        <div className="preventivo-email-field">
          <label>Giorno e ora di invio</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            min={nowLocalInput()}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>

        <div className="preventivo-email-preview-note">
          <FaWhatsapp /> Invio professionale da preventivi@betalumina.com: logo BetaLumina, WhatsApp, firma aziendale e PDF completo.
        </div>
      </div>

      <div className="preventivo-email-main-actions">
        <button type="button" onClick={schedule} disabled={!canOperate || !scheduledAt} className="preventivo-email-schedule-btn">
          <FaClock /> {loading ? "Attendere..." : programButtonLabel}
        </button>
        <button type="button" onClick={sendNow} disabled={!canOperate} className="preventivo-email-send-btn">
          <FaPaperPlane /> Invia subito
        </button>
      </div>
    </div>
  );
};

export default PreventivoEmailSection;
